import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  AlertCircle, 
  Loader2,
  FileText,
  Sparkles,
  CheckCircle,
  Clock,
  ArrowRight,
  Brain,
  Database as DatabaseIcon
} from 'lucide-react';
import { getApiUrl } from '@/config/api';
import Logo from '@/components/Logo';
import { Button } from "@/components/ui/button";
import Cookies from 'js-cookie';

interface ProcessingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  status?: 'pending' | 'processing' | 'completed';
}

const AuthMenuProcessing: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [seasonalMenuId, setSeasonalMenuId] = useState<number | undefined>(undefined);
  const [progress, setProgress] = useState(0);

  // Beautiful processing steps with icons
  const steps: ProcessingStep[] = [
    {
      id: 'starting',
      title: 'Preparing Your Menu',
      description: 'Initializing AI extraction pipeline',
      icon: FileText,
      status: 'pending'
    },
    {
      id: 'downloading',
      title: 'Retrieving Menu Data',
      description: 'Securely downloading your menu files',
      icon: DatabaseIcon,
      status: 'pending'
    },
    {
      id: 'converting',
      title: 'Processing Documents',
      description: 'Converting files for AI analysis',
      icon: Sparkles,
      status: 'pending'
    },
    {
      id: 'extracting',
      title: 'AI Extraction',
      description: 'Understanding your unique offerings',
      icon: Brain,
      status: 'pending'
    },
    {
      id: 'categorizing',
      title: 'Organizing Dishes',
      description: 'Structuring menu for optimal presentation',
      icon: Sparkles,
      status: 'pending'
    },
    {
      id: 'validating',
      title: 'Finalizing Results',
      description: 'Verifying accuracy and completeness',
      icon: CheckCircle,
      status: 'pending'
    }
  ];

  // Timer effect for elapsed time
  useEffect(() => {
    if (!processingComplete && !error) {
      const timer = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [processingComplete, error]);

  // Format time helper
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    // Get processingId from location state
    const procId = location.state?.processingId;
    const seasonalMenuId = location.state?.seasonal_menu_id;

    if (!procId) {
      setError('Processing ID missing. Please try uploading again.');
      return;
    }

    console.log('🚀 Starting authenticated menu processing:', {
      processingId: procId,
      seasonalMenuId: seasonalMenuId
    });

    // Store processingId and seasonalMenuId in state for button to use
    setProcessingId(procId);
    if (seasonalMenuId) {
      setSeasonalMenuId(seasonalMenuId);
    }
    
    // Start polling for status
    startPolling(procId);

    // Cleanup on unmount
    return () => {
      // Cleanup polling if needed
    };
  }, []);

  const startPolling = async (processingId: string) => {
    // Set initial state
    setCurrentStep(0);
    
    // Get auth token
    const token = Cookies.get('auth_token');
    if (!token) {
      setError('Authentication required. Please log in again.');
      navigate('/restaurant-login');
      return;
    }

    // Poll for status every 2 seconds
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(getApiUrl(`menus/${processingId}/status`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to get processing status');
        }

        const status = await response.json();
        
        // Update progress
        const currentProgress = status.progress || 0;
        setProgress(currentProgress);
        setCurrentStep(Math.floor((currentProgress / 100) * steps.length));
        
        // Check if completed
        if (status.status === 'completed') {
          clearInterval(pollInterval);
          setProcessingComplete(true);
          setCurrentStep(steps.length);
          // Don't auto-redirect - let user click the Continue button
        } else if (status.status === 'failed') {
          clearInterval(pollInterval);
          setError(status.error_message || 'Menu processing failed. Please try again.');
          setCurrentStep(-1);
        }
      } catch (error) {
        console.error('Polling error:', error);
        clearInterval(pollInterval);
        setError('Failed to check processing status. Please refresh the page.');
      }
    }, 2000); // Poll every 2 seconds
  };

  const handleTryAgain = () => {
    navigate('/restaurant-menu-upload');
  };

  // Fun facts to display during processing
  const funFacts = [
    "Did you know? Restaurants using Dine AI see 15-30% increase in order value!",
    "Our AI analyzes over 100 attributes per dish to optimize recommendations.",
    "Personalized menus can reduce decision time by 40% for customers.",
    "AI-powered upselling suggestions increase dessert sales by 25%.",
    "Dine AI helps identify your most profitable dishes automatically.",
    "Smart menu optimization can boost revenue by up to 20%.",
    "Our AI learns from millions of dining preferences worldwide."
  ];

  const currentFact = funFacts[Math.floor((elapsedTime / 10) % funFacts.length)];

  return (
    <div className="min-h-screen bg-dineai-blue relative overflow-hidden">
      {/* Beautiful animated background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
      </div>

      {/* Progress bar at top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
        <motion.div 
          className="h-full bg-dineai-yellow"
          initial={{ width: '0%' }}
          animate={{ width: processingComplete ? '100%' : `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <div className="container mx-auto px-4 flex items-center justify-center min-h-screen relative z-10 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-3xl"
        >
          {/* Logo */}
          <div className="fixed top-8 left-8 z-50">
            <Logo className="text-white h-8" />
          </div>

          {/* Beautiful Processing Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/20 shadow-xl">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block bg-dineai-yellow/20 px-3 py-1.5 rounded-full mb-4">
                <span className="text-dineai-yellow font-medium text-xs flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  AI PROCESSING
                </span>
              </div>
              
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
                {error ? 'Processing Error' : processingComplete ? `${seasonalMenuId ? 'Special ' : ''}Menu Processing Complete!` : `Processing Your ${seasonalMenuId ? 'Special ' : ''}Menu`}
              </h1>
              
              {!processingComplete && !error && (
                <div className="flex flex-col items-center justify-center gap-2 text-white/80">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Elapsed time: {formatTime(elapsedTime)}</span>
                  </div>
                  <p className="text-white/70 text-sm">
                    Our AI is carefully analyzing your menu...
                  </p>
                </div>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-6"
              >
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-400 mb-2">Processing Failed</h3>
                    <p className="text-white/80 text-sm mb-4">{error}</p>
                    <Button
                      onClick={handleTryAgain}
                      className="bg-red-500/20 text-white hover:bg-red-500/30 border border-red-500/30"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Beautiful Processing Steps */}
            {!error && (
              <div className="space-y-4 mb-8">
                {steps.map((step, index) => {
                  const isActive = index === currentStep;
                  const isComplete = index < currentStep || processingComplete;
                  const Icon = step.icon;
                  
                  return (
                    <motion.div
                      key={step.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`
                        flex items-start gap-4 p-4 rounded-xl transition-all
                        ${isActive ? 'bg-white/10 border border-dineai-yellow/50' : ''}
                        ${isComplete && !isActive ? 'opacity-70' : ''}
                        ${!isActive && !isComplete ? 'opacity-40' : ''}
                      `}
                    >
                      <div className={`
                        flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                        ${isComplete ? 'bg-green-500/20' : ''}
                        ${isActive ? 'bg-dineai-yellow/20' : ''}
                        ${!isActive && !isComplete ? 'bg-white/10' : ''}
                      `}>
                        {isComplete ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : isActive ? (
                          <Loader2 className="w-5 h-5 text-dineai-yellow animate-spin" />
                        ) : (
                          <Icon className="w-5 h-5 text-white/50" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <h3 className={`
                          font-semibold mb-1
                          ${isActive ? 'text-white' : 'text-white/80'}
                        `}>
                          {step.title}
                        </h3>
                        <p className={`
                          text-sm
                          ${isActive ? 'text-white/80' : 'text-white/60'}
                        `}>
                          {step.description}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Fun Fact */}
            {!processingComplete && !error && (
              <motion.div 
                className="bg-dineai-yellow/10 rounded-xl p-4 mb-6"
                key={currentFact}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-white/90 text-sm text-center">
                  💡 {currentFact}
                </p>
              </motion.div>
            )}

            {/* Success Message & Continue Button */}
            {processingComplete && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-3">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.5 }}
                    className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0"
                  >
                    <CheckCircle className="w-7 h-7 text-green-400" />
                  </motion.div>
                  <div className="text-left">
                    <p className="text-white/90 text-lg font-medium">
                      Your menu has been successfully processed!
                    </p>
                    <p className="text-white/70 text-sm">
                      Processing completed in {formatTime(elapsedTime)}
                    </p>
                  </div>
                </div>
                
                <Button
                  onClick={() => {
                    const params = new URLSearchParams({
                      processing_id: processingId || '',
                      source: 'location'
                    });
                    if (seasonalMenuId) {
                      params.append('seasonal_menu_id', seasonalMenuId.toString());
                    }
                    navigate(`/restaurant-menu-preview?${params.toString()}`);
                  }}
                  className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-3 px-8 text-base font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2 w-full"
                >
                  Review Your {seasonalMenuId ? 'Special' : ''} Menu
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </motion.div>
            )}

          </div>

          {/* Help text */}
          {!processingComplete && !error && (
            <p className="text-center text-white/60 text-sm mt-6">
              AI is extracting your menu items • This usually takes 1-5 minutes
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AuthMenuProcessing;